/**
 * GymScene ('Gym') — the warm gym anteroom of the Badge Circuit (owner decision D17).
 *
 * A small, cozy waiting room, NOT the fight: the leader greets you (name + title in a
 * classic cream DialogBox), and a ▶-cursor Menu offers "Challenge!" / "Maybe later".
 * Choosing to challenge builds the leader's faithful party from data/quests GYM_LEADERS
 * and hands off to the shared BattleScene as a **friendly** (no-stakes) battle; the fight
 * itself lives there. On return the leader plays their warm onWin / onLose lines and — on
 * a win — the scene fires a `battle-win` quest event so the `defeat-gym-leader` objective
 * ticks (the badge itself is granted by the quest turn-in path).
 *
 * Every choice obeys the no-pressure contract (CLAUDE.md + D17): a loss is warm and costs
 * nothing (offer to try again or head back), gyms are never gated (always reachable from
 * the RegionMap), and a player who already holds the badge simply sparr-rematches happily.
 *
 * The renderer owns no rules: parties come from the authored dataset via society/makeInstance,
 * the battle outcome comes from the pure engine, and quest progress is folded by the session.
 * Re-entry safe: Phaser REUSES this scene instance, so create() re-runs on every visit
 * (Gym → Battle → Gym) — resetState()/teardown() drop all prior display + flags so nothing
 * stacks or soft-locks.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { DialogBox } from './ui/DialogBox.js';
import { Menu, type MenuItem } from './ui/Menu.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { getSession } from './session.js';
import type { GameSession } from '@/session';
import { makeInstance } from '@/society';
import { makeRng, hashString } from '@/core';
import type { PokemonInstance, QuestDef, QuestProgress } from '@/shared';
import { GYM_LEADERS, type GymLeader } from '@/data/quests';

/** How the battle result rides back into this scene (see BattleScene.endBattle). */
type GymResult = 'pending' | 'won' | 'lost';

/** Launch/return payload for the Gym scene. */
interface GymSceneData {
  readonly gymId?: string;
  /** Set by BattleScene on the return trip: 'won' | 'lost' (absent on first entry). */
  readonly gymResult?: GymResult;
  /** Present when the return trip is from a RIVAL spar (not the gym leader). */
  readonly rivalResult?: 'pending';
}

/**
 * The quest surface GymScene needs from the session. Agent B adds these to GameSession in
 * parallel; typing them structurally (and guarding at runtime) keeps this file compiling +
 * working whether or not they've landed yet — a missing method is simply a graceful no-op.
 */
interface QuestCapableSession {
  recordQuestEvent?(ev: { kind: 'battle-win'; battleKind: 'gym'; gymId: string }): unknown;
  questState?: { readonly badges: readonly string[] };
}

export class GymScene extends Phaser.Scene {
  private layout!: Layout;
  private reduced = false;
  private session!: GameSession;

  private gymId = '';
  private pendingResult: GymResult | undefined;
  private pendingRival = false;
  private leader?: GymLeader;

  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private emblem?: Phaser.GameObjects.Graphics;
  private backdrop?: Phaser.GameObjects.Graphics;
  private dialog?: DialogBox;
  private menu?: Menu;
  /** Dispatched when the current DialogBox finishes its last page. */
  private afterDialog?: () => void;
  private busy = false;

  constructor() {
    super('Gym');
  }

  init(data: Partial<GymSceneData>): void {
    this.gymId = data.gymId ?? '';
    this.pendingResult = data.gymResult;
    this.pendingRival = data.rivalResult === 'pending';
  }

  create(): void {
    this.resetState();
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();
    this.session = getSession(this);

    this.backdrop = this.add.graphics();
    this.emblem = this.add.graphics();

    const leader = GYM_LEADERS.find((l) => l.gymId === this.gymId);
    this.leader = leader;

    this.heading = this.add
      .text(0, 0, leader ? leader.name : 'A quiet gym', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.subheading = this.add
      .text(0, 0, leader ? leader.title : 'No one is here today — perhaps another time.', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.dialog = new DialogBox(this, 0, 0, {
      width: 340,
      height: 100,
      reducedMotion: this.reduced,
      onDone: () => {
        const fn = this.afterDialog;
        this.afterDialog = undefined;
        if (this.scene.isActive()) fn?.();
      },
    });
    this.dialog.setVisible(false);

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    if (!leader) {
      // Graceful absence (empty GYM_LEADERS, or a stale gymId): a warm line + one way out.
      this.showLines(['This gym is quiet today. Let’s head back and wander a while.'], '', () =>
        this.goHome(),
      );
    } else if (this.pendingRival) {
      // Return trip from a friendly RIVAL spar (not the gym leader) — warm words + tally.
      this.onRivalSparReturn(leader);
    } else if (this.pendingResult === 'won') {
      this.onVictory(leader);
    } else if (this.pendingResult === 'lost') {
      this.onDefeat(leader);
    } else {
      // A warm rival cameo pops in FIRST (once per badge tier), then the normal anteroom.
      this.maybeRivalPreCameo(leader, () => this.onIntro(leader));
    }

    fadeIn(this);
  }

  // ── anteroom phases ───────────────────────────────────────────────────────────

  private onIntro(leader: GymLeader): void {
    const quest = this.mainQuestForGym(leader);
    // A finished-but-not-turned-in badge quest → the leader awards the badge (a warm
    // little moment), not another battle. This also covers a player who won earlier and
    // wandered off before turning in.
    if (quest && this.isReadyToTurnIn(quest)) {
      this.onTurnIn(leader, quest);
      return;
    }
    // A not-yet-started, offerable badge quest → the leader OFFERS it in-character
    // (onStart lines + Accept / Maybe-later), instead of only a bare battle prompt.
    if (quest && this.isOfferable(quest.id)) {
      this.onOffer(leader, quest);
      return;
    }
    // Otherwise (quest in progress, already turned in, or none): the classic anteroom.
    const holds = this.holdsBadge(leader);
    this.showLines(leader.intro, `${leader.name} · ${leader.title}`, () =>
      this.openChallengeMenu(holds),
    );
  }

  private onVictory(leader: GymLeader): void {
    // Fire the defeat-gym-leader progress event once, on arrival from a won gym battle.
    // (Defensive + idempotent if the quest engine already saw it.)
    const qs = this.session as unknown as QuestCapableSession;
    void qs.recordQuestEvent?.({ kind: 'battle-win', battleKind: 'gym', gymId: leader.gymId });

    // If that win just finished the badge quest, the leader turns it in right here so the
    // badge lands in the same warm beat as the victory. Otherwise (warm-up stage not yet
    // done, or no quest) just play the warm onWin lines — no pressure, come back any time.
    const quest = this.mainQuestForGym(leader);
    if (quest && this.isReadyToTurnIn(quest)) {
      this.onTurnIn(leader, quest);
      return;
    }
    this.showLines(leader.onWin, leader.name, () => this.afterWinFlow());
  }

  /** The leader OFFERS their badge quest: onStart lines, then Accept / Maybe-later. */
  private onOffer(leader: GymLeader, quest: QuestDef): void {
    const onStart = quest.stages[0]?.dialogue.onStart ?? [];
    const lines = onStart.length > 0 ? onStart : leader.intro;
    this.showLines(lines, `${leader.name} · ${leader.title}`, () => {
      this.showMenu([
        { label: 'I’d love to — let’s go!', onSelect: () => this.acceptAndChallenge(quest) },
        { label: 'Maybe later', onSelect: () => this.goHome() },
      ]);
    });
  }

  /** Accepting the offer is also accepting the challenge (auto-accept-on-challenge). */
  private acceptAndChallenge(quest: QuestDef): void {
    this.session.acceptQuest(quest.id);
    this.startBattle();
  }

  /** The leader TURNS IN the finished badge quest: onComplete lines, then the badge lands. */
  private onTurnIn(leader: GymLeader, quest: QuestDef): void {
    const finalStage = quest.stages[quest.stages.length - 1];
    const onComplete = finalStage?.dialogue.onComplete ?? leader.onWin;
    this.showLines(onComplete, leader.name, () => {
      void this.turnInAndCelebrate(leader, quest);
    });
  }

  private async turnInAndCelebrate(leader: GymLeader, quest: QuestDef): Promise<void> {
    const result = await this.session.turnInQuest(quest.id);
    if (!this.scene.isActive()) return;
    const badge = this.badgeLabel(leader.badgeId);
    const line = result?.ok
      ? `✦ The ${badge} is yours — safe in your Badge Case, and cheered all round the town.`
      : `You’re always welcome back, friend — whenever the mood takes you.`;
    this.showLines([line], leader.name, () => this.afterWinFlow());
  }

  private onDefeat(leader: GymLeader): void {
    // No-stakes: warm words, zero penalty, and an easy path to try again or head home.
    this.showLines(leader.onLose, leader.name, () => this.openPostMenu('lost'));
  }

  /** The classic ▶-cursor selection box: challenge (or sparr again) / bow out. */
  private openChallengeMenu(holdsBadge: boolean): void {
    this.showMenu([
      {
        label: holdsBadge ? 'A friendly rematch!' : 'Challenge!',
        onSelect: () => this.startBattle(),
      },
      { label: 'Maybe later', onSelect: () => this.goHome() },
    ]);
  }

  private openPostMenu(kind: 'won' | 'lost'): void {
    const items: MenuItem[] = [
      {
        label: kind === 'won' ? 'Battle again' : 'Try again',
        onSelect: () => this.startBattle(),
      },
    ];
    // A warm, opt-in "Spar with {rival}" round — no stakes, never blocks the map exit.
    const rival = this.rivalView();
    if (rival) {
      items.push({ label: `Spar with ${rival.username}`, onSelect: () => this.startRivalSpar() });
    }
    items.push({ label: 'Back to the map', onSelect: () => this.goHome() });
    this.showMenu(items);
  }

  // ── the Warm Rival cameo (rival-arc.md) — a friend cheering you on ──────────────

  /** The pinned rival's view (ensures they're pinned), or null if none this session. */
  private rivalView(): GameSession['rivalView'] {
    try {
      return this.session.rivalView;
    } catch {
      return null;
    }
  }

  /**
   * A brief, one-tap rival pep BEFORE the leader — fires once per badge tier (never twice
   * on a re-entry), lands a warm `rival-cheer` beat in town chat, and ticks the "meet the
   * rival" quest. Never blocks the challenge: if there's no rival or we've already greeted
   * at this tier, it falls straight through to the normal anteroom.
   */
  private maybeRivalPreCameo(_leader: GymLeader, then: () => void): void {
    const rival = this.rivalView();
    if (!rival) {
      then();
      return;
    }
    const tier = this.session.questState.badges.length;
    if (this.session.rivalGreetedTier(tier)) {
      then();
      return;
    }
    this.session.markRivalGreeted(tier);
    this.session.meetRival(); // ticks the "A Familiar Face" / send-off quests
    this.session.rivalCheer('pre-badge');
    const line = this.session.rivalLine('pre-badge');
    this.showLines([line], `${rival.username} · your rival`, then);
  }

  /** After a gym win: a short rival congrats (if any), then the post-battle menu. */
  private afterWinFlow(): void {
    const rival = this.rivalView();
    if (!rival) {
      this.openPostMenu('won');
      return;
    }
    this.session.rivalCheer('post-badge');
    const line = this.session.rivalLine('post-badge');
    this.showLines([line], `${rival.username} · your rival`, () => this.openPostMenu('won'));
  }

  /** Launch a no-stakes friendly spar vs the rival's scaled party (cozy so it's fun). */
  private startRivalSpar(): void {
    if (this.busy) return;
    const rival = this.rivalView();
    const party = this.session.rivalSpar();
    if (!rival || party.length === 0) {
      this.showLines(['Maybe another round another day — see you out there, friend!'], '', () =>
        this.openPostMenu('won'),
      );
      return;
    }
    this.busy = true;
    this.hideMenu();
    this.dialog?.setVisible(false);
    goTo(this, 'Battle', {
      data: {
        kind: 'friendly',
        opponentParty: party,
        opponentName: `${rival.username} (your rival)`,
        returnScene: 'Gym',
        // gymId lets BattleScene report a won/lost result; rivalResult routes it to the
        // spar handler (NOT the gym-leader victory path, so no gym badge is credited).
        returnData: { gymId: this.gymId, rivalResult: 'pending' as const },
        difficultyOverride: 'cozy',
      },
    });
  }

  /** Return trip from a rival spar: record it warmly (win OR lose), then the post menu. */
  private onRivalSparReturn(_leader: GymLeader): void {
    const won = this.pendingResult === 'won';
    this.session.recordRivalSpar(won);
    const rival = this.rivalView();
    const speaker = rival ? `${rival.username} · your rival` : 'Your rival';
    const line = this.session.rivalLine(won ? 'spar-win' : 'spar-loss');
    this.showLines([line], speaker, () => this.openPostMenu('won'));
  }

  // ── the hand-off to the shared Battle scene ─────────────────────────────────────

  private startBattle(): void {
    if (this.busy) return;
    const leader = this.leader;
    if (!leader) {
      this.goHome();
      return;
    }
    const party = this.buildLeaderParty(leader);
    if (party.length === 0) {
      // Defensive: the dataset lacked every species in the party — keep it warm.
      this.showLines(
        [`${leader.name}: my team’s resting just now — come spar another day!`],
        leader.name,
        () => this.openPostMenu('lost'),
      );
      return;
    }
    this.busy = true;
    this.hideMenu();
    this.dialog?.setVisible(false);
    goTo(this, 'Battle', {
      data: {
        kind: 'friendly',
        opponentParty: party,
        opponentName: `${leader.name}, ${leader.title}`,
        returnScene: 'Gym',
        returnData: { gymId: leader.gymId, gymResult: 'pending' as GymResult },
        // Respect the player's Cozy↔Spicy dial for the gym fight, like any other battle.
        difficultyOverride: this.session.currentSettings.difficulty,
      },
    });
  }

  /**
   * Build the leader's faithful party from the authored {speciesId, level} list via
   * society/makeInstance. makeInstance mints move slots with pp/maxPp = 0 (that layer is
   * pure and has no move data), so — exactly like GameSession.friendlyParty — we hydrate
   * real PP here from the DataIndex. Any species the dataset lacks is skipped (never a
   * crash on a thin fixtures build). Deterministic per gym so the team is stable on rematch.
   */
  private buildLeaderParty(leader: GymLeader): PokemonInstance[] {
    const index = this.session.bundle.index;
    const rng = makeRng((this.session.seed ^ hashString(`gym|${leader.gymId}`)) >>> 0);
    const owner = { id: `gym-${leader.gymId}`, name: leader.name };
    const party: PokemonInstance[] = [];
    for (const spec of leader.party) {
      const species = index.speciesById(spec.speciesId);
      if (!species) continue;
      const mon = makeInstance(rng, species, spec.level, owner);
      mon.currentHp = mon.maxHp;
      mon.status = 'none';
      mon.statusTurns = 0;
      mon.moves = mon.moves.map((slot) => {
        if (slot.maxPp > 0) return { ...slot, pp: slot.maxPp };
        const maxPp = index.moveById(slot.moveId)?.pp ?? 5;
        return { ...slot, pp: maxPp, maxPp };
      });
      party.push(mon);
    }
    return party;
  }

  private holdsBadge(leader: GymLeader): boolean {
    const qs = this.session as unknown as QuestCapableSession;
    return (qs.questState?.badges ?? []).includes(leader.badgeId);
  }

  // ── quest wiring (D17: offer + turn-in through the leader) ───────────────────────

  /** The main badge quest whose `badge` reward matches this gym (or none). */
  private mainQuestForGym(leader: GymLeader): QuestDef | undefined {
    return this.session.questDefs.find(
      (q) => q.kind === 'main' && q.reward.kind === 'badge' && q.reward.badgeId === leader.badgeId,
    );
  }

  /** The player's live progress on a quest (undefined when not started). */
  private questProgressFor(questId: string): QuestProgress | undefined {
    return this.session.questState.active.find((q) => q.questId === questId);
  }

  /** Is this quest not-yet-started and offerable right now (soft visibility only)? */
  private isOfferable(questId: string): boolean {
    return this.session.offerableQuests().some((q) => q.id === questId);
  }

  /** All stages cleared, reward not yet granted → the leader can turn it in. */
  private isReadyToTurnIn(quest: QuestDef): boolean {
    const p = this.questProgressFor(quest.id);
    return !!p && !p.turnedIn && p.stageIndex >= quest.stages.length;
  }

  /** A friendly display name for a badge id ('meadow-badge' → 'Meadow Badge'). */
  private badgeLabel(badgeId: string): string {
    return badgeId
      .split('-')
      .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  // ── dialog + menu helpers ───────────────────────────────────────────────────────

  private showLines(lines: readonly string[], speaker: string, after: () => void): void {
    this.afterDialog = after;
    const pages = lines.length > 0 ? lines : ['…'];
    this.dialog?.show(pages, speaker);
  }

  private showMenu(items: readonly MenuItem[]): void {
    this.hideMenu();
    const menu = new Menu(this, 0, 0, {
      items,
      reducedMotion: this.reduced,
      width: Math.min(this.layout.safe.width - SPACING.md * 2, 300),
      onCancel: () => this.goHome(),
    });
    this.menu = menu;
    this.positionMenu();
    menu.focus();
  }

  private hideMenu(): void {
    this.menu?.destroy();
    this.menu = undefined;
  }

  private goHome(): void {
    if (this.busy) return;
    this.busy = true;
    this.hideMenu();
    goTo(this, 'RegionMap');
  }

  // ── layout ──────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.xl;

    this.drawBackdrop();

    this.heading?.setPosition(cx, top);
    this.subheading?.setPosition(cx, top + FONT.size.lg + SPACING.sm);
    this.drawEmblem(cx, top + FONT.size.lg + SPACING.sm + FONT.size.sm + SPACING.xl);

    // Dialog pinned to the bottom; the selection menu floats just above it.
    const dialogH = 100;
    const dialogY = safe.y + safe.height - dialogH / 2 - SPACING.md;
    this.dialog?.setPosition(cx, dialogY);
    this.positionMenu();
  }

  private positionMenu(): void {
    if (!this.menu) return;
    const safe = this.layout.safe;
    const dialogH = 100;
    const dialogTop = safe.y + safe.height - dialogH - SPACING.md;
    this.menu.setPosition(this.layout.centerX, dialogTop - this.menu.boxHeight / 2 - SPACING.sm);
  }

  private drawBackdrop(): void {
    if (!this.backdrop) return;
    const safe = this.layout.safe;
    const g = this.backdrop;
    g.clear();
    // A calm layered room: soft ground with a warm gym rug band in the middle.
    g.fillStyle(COLORS.surface, 1);
    g.fillRect(safe.x, safe.y, safe.width, safe.height);
    g.fillStyle(COLORS.surfaceAlt, 0.5);
    g.fillRoundedRect(
      safe.x + SPACING.md,
      safe.y + safe.height * 0.32,
      safe.width - SPACING.md * 2,
      safe.height * 0.34,
      RADIUS.lg,
    );
  }

  /** A small badge-shaped gold emblem — a gentle nod to the trophy you might earn. */
  private drawEmblem(x: number, y: number): void {
    if (!this.emblem) return;
    const g = this.emblem;
    g.clear();
    const r = 26;
    g.fillStyle(COLORS.scrim, 0.2);
    g.fillCircle(x, y + 3, r);
    g.fillStyle(COLORS.warm, 1);
    g.fillCircle(x, y, r);
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeCircle(x, y, r);
    // A soft inner star-ish glint (four petals) so it reads as a badge, not just a dot.
    g.fillStyle(COLORS.boxFill, 0.9);
    g.fillCircle(x, y, r * 0.42);
  }

  // ── re-entry safety ──────────────────────────────────────────────────────────────

  private resetState(): void {
    this.heading = undefined;
    this.subheading = undefined;
    this.emblem = undefined;
    this.backdrop = undefined;
    this.dialog = undefined;
    this.menu = undefined;
    this.afterDialog = undefined;
    this.leader = undefined;
    this.busy = false;
  }

  private teardown(): void {
    this.tweens.killAll();
    this.hideMenu();
    this.dialog?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.emblem?.destroy();
    this.backdrop?.destroy();
    this.dialog = undefined;
    this.heading = undefined;
    this.subheading = undefined;
    this.emblem = undefined;
    this.backdrop = undefined;
    this.afterDialog = undefined;
  }
}
